import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, signOut, getProfile, getNotifications, markNotificationRead } from '../utils/supabase';

const TopBar = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadUserData = async () => {
    const { user } = await getCurrentUser();
    setUser(user);
    if (user) {
      const { data } = await getProfile(user.id);
      setProfile(data);
      await loadNotifications();
    }
  };

  const loadNotifications = async () => {
    if (!user) return;
    const { data } = await getNotifications(user.id);
    setNotifications(data || []);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container-custom py-3 flex items-center justify-between">
        <Link to="/" className="text-2xl font-bold text-primary flex items-center gap-2">
          <span className="text-3xl">📝</span>
          <span className="hidden sm:inline">TodoSocial</span>
        </Link>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-full hover:bg-gray-100 transition"
            >
              <span className="text-2xl">🔔</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl border border-gray-200 animate-fade-in">
                <div className="p-3 border-b border-gray-200">
                  <h3 className="font-semibold">Notifications</h3>
                </div>
                <div className="p-2">
                  {notifications.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No notifications</p>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-2 rounded-lg mb-1 cursor-pointer hover:bg-gray-50 ${!notif.is_read ? 'bg-blue-50' : ''}`}
                        onClick={async () => {
                          if (!notif.is_read) {
                            await markNotificationRead(notif.id);
                            await loadNotifications();
                          }
                        }}
                      >
                        <p className="text-sm">{notif.message}</p>
                        <span className="text-xs text-gray-400">
                          {new Date(notif.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-100 transition"
              >
                <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                  {profile?.full_name ? getInitials(profile.full_name) : '?'}
                </div>
                <span className="hidden md:inline text-sm font-medium">
                  {profile?.full_name?.split(' ')[0] || 'User'}
                </span>
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 animate-fade-in">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 hover:bg-gray-50 rounded-t-lg"
                    onClick={() => setShowDropdown(false)}
                  >
                    👤 Profile
                  </Link>
                  <Link
                    to="/profile/todo"
                    className="block px-4 py-2 hover:bg-gray-50"
                    onClick={() => setShowDropdown(false)}
                  >
                    📋 My Todos
                  </Link>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      handleSignOut();
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-50 rounded-b-lg text-red-600"
                  >
                    🚪 Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/auth"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
